import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const updateBackendLocation = async (latitude, longitude) => {
    try {
      await axios.post(
        `${API_URL}/api/profile/location`,
        { latitude, longitude },
        {
          withCredentials: true,  // Use httpOnly cookie for auth
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Location updated on server');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to update location on server:', error);
      }
    }
  };

  const requestLocation = (silent = false) => {
    // Only show loading screen on initial load, not periodic updates
    if (!silent && isInitialLoad) {
      setLoading(true);
    }
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      setPermission('denied');
      setIsInitialLoad(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setPermission('granted');
        setLoading(false);
        setIsInitialLoad(false);
        
        // Update backend with location
        updateBackendLocation(latitude, longitude);
      },
      (error) => {
        setError(error.message);
        setPermission('denied');
        setLoading(false);
        setIsInitialLoad(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 3600000 // 1 hour cache
      }
    );
  };

  // Auto-request on mount
  useEffect(() => {
    requestLocation(false); // Initial load - show loading screen
    
    // Update location every hour while app is open (silent updates)
    const interval = setInterval(() => {
      if (permission === 'granted') {
        requestLocation(true); // Background update - NO loading screen
      }
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  return {
    location,
    error,
    loading,
    permission,
    requestLocation
  };
};
